/**
 * Traffic value — the merchant "Tu tráfico" surface (ACQUISITION-VALUE-SPEC.md).
 *
 * Two measured axes per campaign — do they engage, do they buy — and never a
 * budget recommendation: the person holding the invoice does that arithmetic.
 * Placement and stability are computed server-side with `bbrope.compareArms`
 * (spec §1.2/§2.3) so nothing statistical happens in the browser; the client
 * owns wording only.
 *
 * Backend status: the reader route and the tag storage are P1/P2 of the spec and
 * are NOT built yet. The screen degrades to its empty state until they land.
 */

import { apiClient, handleApiResponse } from './client'
import type { ApiResponse } from '@/types'

/** Where a row sits on one axis. `unplaced` = compareArms returned inconclusive. */
export type Placement = 'above' | 'below' | 'unplaced'

/**
 * The 2x2. `sin_datos` covers both "unplaced on both axes" and the half-placed
 * row (behaviour known, sales not yet) — the row copy distinguishes them from
 * `placement`, so the quadrant stays a four-cell model plus one honest gap.
 */
export type Quadrant = 'valioso' | 'interesa_no_cierra' | 'llega_decidido' | 'no_sirve' | 'sin_datos'

/** Window-over-window, ROPE-guarded. The anti-noise output for daily deciders. */
export type Stability = 'sin_cambios' | 'cambio' | 'sin_datos'

/** Human-supplied expectation. Never inferred from the campaign name (spec §1.7). */
export type Objective = 'ventas' | 'reconocimiento' | 'unknown'

/** One destination a campaign sends traffic to. `share` is over the campaign's own visits. */
export interface CampaignLanding {
  path: string
  sessions: number
  share: number | null
}

export interface TrafficCampaignRow {
  channel: string
  campaign: string
  medium: string | null
  /**
   * Where this campaign lands people — top 3 by visits, biggest first. The shares do not sum
   * to 1: the producer keeps only the top 5 paths per campaign per day, so a scattered
   * campaign's tail is real but unlisted. `landing_coverage` says how much is accounted for.
   */
  landings?: CampaignLanding[] | null
  /** Classified destination kind (home / product / category / other). `other` means the
   *  classifier didn't recognise a custom landing page — the path is still exact. */
  landing_kind?: string | null
  landing_coverage?: number | null
  sessions: number
  conversions: number
  /** Absent on days predating the producer counters — the axis degrades, never reads 0. */
  revenue?: number
  engaged?: number
  shoppers?: number
  value_per_visit: number | null
  engagement_rate: number | null
  conversion_rate: number | null
  placement: { engagement: Placement; value: Placement }
  quadrant: Quadrant
  stability: Stability
  /**
   * The synthetic "el resto de {canal}" row: untagged sessions + campaigns folded past the
   * top-N cap. Real, measured traffic that simply can't be individualised — so it is shown
   * and plotted like any other row, but can't be tagged, shared, or acted on by name.
   */
  is_rest?: boolean
  /**
   * The rest row's composition. Untagged visits carry no `utm_campaign`, so they can never
   * be listed by name; the folded ones are real campaigns the producer summed past its
   * top-N cap. Present only on `is_rest` rows.
   */
  rest_untagged_sessions?: number
  rest_folded_sessions?: number
  /**
   * Campaigns the CHART folded into this bag because each had too few visits for its own
   * bubble to mean anything (`MIN_VISITS_TO_PLOT_ALONE`). Client-side only — the list shows
   * them individually, since a row of text claims no position.
   */
  rest_small_campaigns?: number
  rest_small_sessions?: number
  /**
   * The channel taken whole, carried on `TrafficChannelGroup.rollup` — never a campaign,
   * never in `campaigns`. It is the map's floor: plotted only when no row of its channel
   * plots, so a fully-tagged channel whose sends are individually too small to place still
   * appears. Can't be tagged or forwarded, and never becomes a finding.
   */
  is_channel_rollup?: boolean
  /** How many named campaigns the roll-up is standing for. */
  rollup_campaigns?: number
  /**
   * Which peer set placed this row. Absent = the rest of its own channel (the norm).
   * `'store'` = the row IS its channel (no named campaigns, or a roll-up), so it was placed
   * against the rest of the store — a within-channel comparison would have been vacuous.
   */
  placed_vs?: 'store'
}

export interface TrafficChannelGroup {
  channel: string
  sessions: number
  value_per_visit: number | null
  campaigns_total: number
  /** Rows that earned a placement on at least one axis. Drives the map's 4+ gate. */
  campaigns_measured: number
  /** Quadrant of the channel taken as a whole — the merged roll-up in the header. */
  dominant_quadrant: Quadrant | null
  /** Traffic arrives with no `utm_campaign` at all: the campaign list is empty by construction. */
  sin_utm: boolean
  campaigns: TrafficCampaignRow[]
  /**
   * The channel as one row, placed against the rest of the store. Deliberately not in
   * `campaigns` (it would double-count the channel against its own rows in the list) —
   * `mapRows` falls back to it so the chart can never omit a channel that has traffic.
   */
  rollup?: TrafficCampaignRow | null
}

/**
 * What the server decided is worth saying. The client owns the wording (same split
 * as MerchantHome's STATUS_LINE), so copy changes never need a lambda deploy.
 */
export type FindingKind =
  | 'valioso'
  | 'no_sirve'
  | 'interesa_no_cierra'
  | 'interesa_ventas'
  | 'reconocimiento_ok'
  | 'desalineada'
  | 'sin_utm'

export interface TrafficFinding {
  kind: FindingKind
  channel: string
  /** Absent on `sin_utm`, which is about a whole channel. */
  campaign?: string
  row?: TrafficCampaignRow
}

export interface TrafficValueResponse {
  store_id: string
  window_days: number
  /** False while the window still has days predating the producer counters (spec §2.4). */
  window_complete: boolean
  unattributed_conversion_pct: number | null
  origin_coverage_pct: number | null
  /** At most two, already ranked. */
  findings: TrafficFinding[]
  channels: TrafficChannelGroup[]
}

/** Stored with its audit trail — same shape on the wire as at rest, no flattening layer. */
export interface CampaignTag {
  objective: Objective
  updated_at?: string
  updated_by?: string
}

export type CampaignTags = Record<string, CampaignTag>

/** Tags are keyed per channel: the same campaign name can exist in two channels. */
export function tagKey(channel: string, campaign: string): string {
  return `${channel}::${campaign}`
}

class TrafficService {
  async getTrafficValue(storeId: string, windowDays = 7): Promise<TrafficValueResponse> {
    const res = await apiClient.get<ApiResponse<TrafficValueResponse>>(
      '/api/store/traffic/value',
      { store_id: storeId, window: windowDays }
    )
    return handleApiResponse(res)
  }

  // NOTE: the tag routes are served by goals-handler, which returns the raw object —
  // NOT the reader's { success, data } envelope. So these deliberately skip
  // handleApiResponse; running them through it would reject every valid response.
  async getCampaignTags(storeId: string): Promise<CampaignTags> {
    const res = await apiClient.get<{ tags: CampaignTags }>(
      `/api/stores/${storeId}/campaign-tags`
    )
    return res?.tags || {}
  }

  /** Partial upsert — one or many. "No sé" is stored as `unknown` so we stop asking. */
  async setCampaignTags(
    storeId: string,
    tags: Array<{ channel: string; campaign: string; objective: Objective }>
  ): Promise<CampaignTags> {
    const res = await apiClient.put<{ tags: CampaignTags }>(
      `/api/stores/${storeId}/campaign-tags`,
      { tags }
    )
    return res?.tags || {}
  }
}

export const trafficService = new TrafficService()
